import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, LabelHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

const FIELD_CLASS =
  'flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-900 ' +
  'placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-[var(--brand-accent)] disabled:cursor-not-allowed disabled:opacity-50 ' +
  'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(FIELD_CLASS, className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn(FIELD_CLASS, 'h-auto min-h-20 py-2', className)} {...props} />;
  },
);

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('block text-xs font-medium text-neutral-600 dark:text-neutral-400', className)}
      {...props}
    />
  );
}

/**
 * Pole z etykieta powiazana przez `htmlFor` — dostepnosc z klawiatury w
 * formularzu M2 stoi na tym, ze KAZDA kontrolka ma etykiete i widoczny focus.
 */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('space-y-1', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children(id)}
      {hint === undefined ? null : (
        <p className="text-[11px] text-neutral-500 dark:text-neutral-500">{hint}</p>
      )}
    </div>
  );
}

export { FIELD_CLASS };
